/**
 * Warehouse Catalog (Spec 201)
 *
 * Parity with the Lakehouse Catalog: browse all warehouses and manage each one's protection tier
 * (recovery objective) + retention policy — the source-level SLA assignment. Tiers + retention are
 * defined centrally in Backup → Settings; this page assigns them per warehouse. Built from the kit.
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Warehouse as WarehouseIcon, Shield, AlertTriangle } from 'lucide-react';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Select,
  Modal,
  StatCard,
  AppTooltip,
  LoadingState,
  ErrorState,
  EmptyState,
} from '@/components/ui';
import apiClient from '../services/api';
import { tiersApi } from '../api/tiers';
import { retentionApi } from '../api/retention';
import { assignmentApi } from '../api/assignment';
import { useAuth } from '../hooks/useAuth';
import { ListFilterBar, textMatch } from '../components/common/ListFilterBar';
import { ProtectionTierBadge, tierIcon } from '../components/common/ProtectionTierBadge';
import { formatWarehouseSize, unmeasuredSizeNote, warehouseSizeNote } from '../utils/entitySize';
import { useBackupActivity } from '../store/backupActivityStore';

interface WarehouseRow {
  id: string;
  display_name: string;
  workspace_name: string | null;
  status: string;
  storage_size_gb: number | null;
  size_measurement_error: string | null;
  table_count: number | null;
  protection_tier: string | null;
  retention_policy_id: string | null;
}

const fmtGb = (n: number | null): string =>
  n == null ? '—' : n >= 1024 ? `${(n / 1024).toFixed(1)} TB` : n >= 10 ? `${n.toFixed(0)} GB` : `${n.toFixed(1)} GB`;

const fetchWarehouses = async (): Promise<WarehouseRow[]> => {
  const r = await apiClient.get<{ success: boolean; data: WarehouseRow[] }>('/api/v1/warehouses?pageSize=500');
  return r.data;
};

interface WarehouseCatalogStats {
  total: number;
  active: number;
  inactive: number;
  byTier: { Gold: number; Silver: number; Bronze: number; Starter: number };
  reviewRequired: number;
  totalSizeGb: number;
  /** Active warehouses the last collection did not size; the total counts their last known size (T159 item 32). */
  sizeNotMeasuredCount?: number;
}

const fetchWarehouseStats = async (): Promise<WarehouseCatalogStats> => {
  const r = await apiClient.get<{ success: boolean; data: WarehouseCatalogStats }>('/api/v1/warehouses/stats');
  return r.data;
};

/**
 * Spec 216 T029: renders standalone (legacy /warehouses/catalog, now
 * redirected) OR embedded as the "Protection" tab of the Warehouse List.
 */
export const WarehouseCatalog: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { hasRole } = useAuth();
  const canManage = hasRole(['admin', 'operator']);
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<WarehouseRow | null>(null);
  const [tier, setTier] = useState('');
  const [retention, setRetention] = useState('');
  const [retentionTouched, setRetentionTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const recordActivity = useBackupActivity((s) => s.add);

  const warehouses = useQuery({ queryKey: ['warehouse-catalog'], queryFn: fetchWarehouses });
  const statsQ = useQuery({ queryKey: ['warehouse-catalog-stats'], queryFn: fetchWarehouseStats });
  const tiersQ = useQuery({ queryKey: ['backup', 'tiers'], queryFn: tiersApi.list });
  const policiesQ = useQuery({ queryKey: ['backup', 'retention-policies'], queryFn: retentionApi.listPolicies });
  // Backup-enabled = the warehouse has a backup destination assigned. Only then does a tier change
  // trigger a full backup.
  const enabledQ = useQuery({ queryKey: ['backup', 'assignable-entities'], queryFn: assignmentApi.assignableEntities });
  const enabledIds = useMemo(
    () => new Set((enabledQ.data ?? []).filter((e) => e.entityType === 'warehouse' && e.assigned).map((e) => e.entityId.toLowerCase())),
    [enabledQ.data]
  );

  const tierRpo = (name: string | null): string => {
    const t = tiersQ.data?.find((x) => x.tierName === name);
    if (!t || t.rpoHours == null) return '';
    const h = t.rpoHours;
    return h >= 24 && h % 24 === 0 ? `${h / 24}d` : `${h}h`;
  };
  const polName = (id: string | null): string => {
    if (!id) return 'inherit default';
    return policiesQ.data?.find((p) => p.id === id)?.name ?? 'custom';
  };

  const rows = useMemo(
    () => (warehouses.data ?? []).filter((w) => textMatch(q, w.display_name, w.workspace_name, w.protection_tier)),
    [warehouses.data, q]
  );

  const openEdit = (w: WarehouseRow) => {
    setEditing(w);
    setTier(w.protection_tier ?? 'Bronze');
    setRetention(w.retention_policy_id ?? '');
    setRetentionTouched(false);
    setConfirming(false);
  };
  const close = () => {
    setEditing(null);
    setConfirming(false);
  };

  const tierChanged = !!editing && !!tier && tier !== editing.protection_tier;
  const backupEnabled = !!editing && enabledIds.has(editing.id.toLowerCase());
  // A tier change is the recovery objective changing, so — when backup is enabled — it immediately
  // triggers a full backup. Confirm first (warn of the consequence), then set the tier + kick it off.
  const willTriggerBackup = tierChanged && backupEnabled;

  const onSaveClick = () => {
    if (willTriggerBackup && !confirming) {
      setConfirming(true);
      return;
    }
    void doSave();
  };

  const doSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (tierChanged) await tiersApi.setEntityTier('warehouse', editing.id, tier);
      if (retentionTouched) await retentionApi.setEntityRetention('warehouse', editing.id, retention || null);
      if (willTriggerBackup) {
        // Immediately trigger a full backup so the new tier's objective is met from a fresh baseline,
        // and record it so the global activity drawer pops open with live progress (FR-040).
        const r = await assignmentApi
          .triggerBackup('warehouse', editing.id, 'full')
          .catch(() => undefined);
        if (r?.launched && r.executionName) {
          recordActivity({
            executionName: r.executionName,
            verb: 'Backup',
            subject: editing.display_name,
            entityType: 'warehouse',
            entityId: editing.id,
          });
        }
      }
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['warehouse-catalog'] }),
        qc.invalidateQueries({ queryKey: ['backup', 'backup-configs'] }),
        qc.invalidateQueries({ queryKey: ['backup', 'dash', 'readiness'] }),
      ]);
      close();
    } finally {
      setSaving(false);
    }
  };

  if (warehouses.isError) return <ErrorState onRetry={() => warehouses.refetch()} />;
  // Spec 298 FR-006 (row 44): no list yet is pending, fetching or paused (isLoading is false while paused); never
  // "No warehouses found" or a filter total of 0.
  if (!warehouses.data) return <LoadingState rows={6} />;
  const listTotal = warehouses.data.length;

  // Embedded (Protection tab of the Warehouse List): no PageLayout chrome.
  if (embedded) {
    return <>{renderBody()}</>;
  }

  return (
    <PageLayout
      title="Warehouse Catalog"
      description="Manage protection tiers and retention policies for all warehouses."
      icon={<WarehouseIcon />}
    >
      {renderBody()}
    </PageLayout>
  );

  function renderBody() {
    return (
      <>
      {statsQ.data && (
        <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-6">
          <StatCard label="Total Warehouses" value={statsQ.data.total} />
          <StatCard
            label="Total Backup Size"
            value={fmtGb(statsQ.data.totalSizeGb)}
            subtext={unmeasuredSizeNote(statsQ.data.sizeNotMeasuredCount)}
            valueClassName="text-[var(--color-accent)]"
          />
          <StatCard
            label="Gold Tier"
            value={statsQ.data.byTier?.Gold ?? 0}
            valueClassName="text-amber-600 dark:text-amber-400"
          />
          <StatCard
            label="Silver Tier"
            value={statsQ.data.byTier?.Silver ?? 0}
            valueClassName="text-[var(--color-text-secondary)]"
          />
          <StatCard
            label="Bronze Tier"
            value={statsQ.data.byTier?.Bronze ?? 0}
            valueClassName="text-orange-600 dark:text-orange-400"
          />
          <AppTooltip title="Starter = default tier — assign a protection tier to set its recovery objective">
            <div>
              <StatCard
                label="Starter Tier"
                value={statsQ.data.byTier?.Starter ?? 0}
                valueClassName={(statsQ.data.byTier?.Starter ?? 0) > 0 ? 'text-yellow-600 dark:text-yellow-400' : undefined}
              />
            </div>
          </AppTooltip>
        </div>
      )}
      <Card padding="none">
        {rows.length === 0 && !q ? (
          <EmptyState title="No warehouses found" />
        ) : (
          <div className="p-4">
            <ListFilterBar query={q} onQuery={setQ} placeholder="Search warehouses…" count={rows.length} total={listTotal} />
            <DataTable<WarehouseRow>
              minWidth="56rem"
              columns={[
                {
                  key: 'display_name',
                  header: 'Warehouse',
                  width: '32%',
                  render: (w) => (
                    <span className="block min-w-0">
                      <span className="block truncate font-medium text-[var(--color-text-primary)]">{w.display_name}</span>
                      <span className="block truncate text-xs text-[var(--color-text-secondary)]">{w.workspace_name ?? '—'}</span>
                    </span>
                  ),
                },
                {
                  key: 'protection_tier',
                  header: 'Protection tier',
                  width: '18%',
                  render: (w) => (
                    <span className="flex items-center gap-2">
                      <ProtectionTierBadge tier={w.protection_tier} size="sm" />
                      {tierRpo(w.protection_tier) && <span className="text-xs text-[var(--color-text-secondary)]">{tierRpo(w.protection_tier)} RPO</span>}
                    </span>
                  ),
                },
                {
                  key: 'retention_policy_id',
                  header: 'Retention',
                  width: '28%',
                  sortable: false,
                  render: (w) => (
                    <span className="block truncate text-sm text-[var(--color-text-secondary)]" title={polName(w.retention_policy_id)}>
                      {polName(w.retention_policy_id)}
                    </span>
                  ),
                },
                {
                  key: 'storage_size_gb',
                  header: 'Size',
                  width: '11%',
                  align: 'right',
                  sortable: false,
                  render: (w) => {
                    const size = formatWarehouseSize(w.storage_size_gb, w.table_count, w.size_measurement_error);
                    if (size.state === 'sized' || size.state === 'measured-tiny') return <span className="tabular-nums text-[var(--color-text-primary)]" title={size.state === 'measured-tiny' ? 'Measured — real footprint under 0.01 GB' : undefined}>{size.text}</span>;
                    if (size.state === 'empty') return <span className="text-[var(--color-text-secondary)]" title="No tables — this warehouse holds no data">Empty</span>;
                    return <span className="italic text-[var(--color-status-warning)]" title={warehouseSizeNote(size)}>Not measured</span>;
                  },
                },
                {
                  key: 'actions',
                  header: '',
                  width: '12%',
                  align: 'right',
                  sortable: false,
                  render: (w) =>
                    canManage ? (
                      <Button variant="outline" size="sm" onClick={() => openEdit(w)}>
                        <Shield className="h-4 w-4" /> Edit
                      </Button>
                    ) : null,
                },
              ]}
              rows={rows}
              rowKey={(w) => w.id}
            />
          </div>
        )}
      </Card>

      <Modal
        isOpen={!!editing}
        onClose={close}
        title={`Protect — ${editing?.display_name ?? ''}`}
        size="md"
        footer={
          confirming ? (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirming(false)} disabled={saving}>Back</Button>
              <Button variant="primary" onClick={onSaveClick} disabled={saving}>{saving ? 'Working…' : 'Confirm & back up now'}</Button>
            </div>
          ) : (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={close}>Cancel</Button>
              <Button variant="primary" onClick={onSaveClick} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          )
        }
      >
        {confirming ? (
          <div className="flex items-start gap-3 rounded-md border border-[var(--color-status-warning)] bg-[var(--color-bg-subtle)] px-4 py-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-status-warning)]" />
            <div className="text-sm">
              <p className="font-medium text-[var(--color-text-primary)]">
                Changing the protection tier to <strong>{tier}</strong> will immediately start a <strong>full backup</strong> of{' '}
                <strong>{editing?.display_name}</strong>.
              </p>
              <p className="mt-1 text-[var(--color-text-secondary)]">
                A full backup can take a while and consume storage. The new tier&apos;s objective (RPO/RTO) applies once it completes.
                Continue?
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]">Protection tier (recovery objective)</label>
              <Select
                value={tier}
                onChange={(e) => setTier(e.target.value)}
                options={(tiersQ.data ?? []).map((t) => ({
                  value: t.tierName,
                  label: `${tierIcon(t.tierName)} ${t.tierName}${tierRpo(t.tierName) ? ` · ${tierRpo(t.tierName)} RPO` : ''}`,
                }))}
              />
              {willTriggerBackup && (
                <p className="mt-1 flex items-center gap-1 text-xs text-[var(--color-status-warning)]">
                  <AlertTriangle className="h-3.5 w-3.5" /> Saving a tier change triggers a full backup.
                </p>
              )}
              {tierChanged && !backupEnabled && (
                <p className="mt-1 text-xs text-[var(--color-text-secondary)]">Backup isn&apos;t enabled for this warehouse — no backup will run.</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--color-text-secondary)]">Retention policy</label>
              <Select
                value={retention}
                onChange={(e) => {
                  setRetention(e.target.value);
                  setRetentionTouched(true);
                }}
                options={[
                  { value: '', label: 'Inherit default' },
                  ...(policiesQ.data ?? [])
                    .filter((p) => !p.appliesToEntityType || p.appliesToEntityType === 'warehouse')
                    .map((p) => ({ value: p.id, label: p.name })),
                ]}
              />
            </div>
            <p className="text-xs text-[var(--color-text-secondary)]">
              Tiers + retention policies are defined in Backup → Settings. Leave retention on Inherit to use the default for warehouses.
            </p>
          </div>
        )}
      </Modal>
      </>
    );
  }
};

export default WarehouseCatalog;
